/**
 *
 *  Homepage
 *  Component
 *
 *  Created by Vladimir Shishlyannikov on 23/03/2023.
 *  Copyright (c) 2023-present iWoo Ltd. All rights reserved.
 *
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import Cookies from "js-cookie";
import Modal from "react-modal";
import Slider from "react-slick";
import { Title, Meta } from "react-meta-elements";

import { useAppState } from "../../state";
import { useRegion } from "../../context/RegionProvider";

// Components
import Header from "./Header/Header";
import Footer from "../Footer/Footer";
import Button from "../Button/Button";
import ScrollToTopOnMount from "../ScrollToTopOnMount/ScrollToTopOnMount";
import ClampLines from "../ClampLines/ClampLines";
import AskConciergeModal from "./AskConciergeModal/AskConciergeModal";
import { SliderNextButton } from "../SliderNextButton/SliderNextButton";
import { SliderPrevButton } from "../SliderPrevButton/SliderPrevButton";
import DisciplineSlide from "../DisciplineSlide/DisciplineSlide";
import CityPickerButton from "../CityPickerButton/CityPickerButton";
import MovingBar from "../MovingBar/MovingBar";
import LogoBar from "../LogoBar/LogoBar";
import HeadlineWithSeparator from "../HeadlineWithSeparator/HeadlineWithSeparator";
import UpcomingExperiencesSlider from "../LifestyleLP/UpcomingExperiencesSlider/UpcomingExperiencesSlider";
import { EventCard } from "../LifestyleLP/LifestyleLP";
import CorporateContactForm from "../CorporateContactForm/CorporateContactForm";
import ScrollingIndicator from "./ScrollingIndicator/ScrollingIndicator";
import HomeStoriesCarousel from "./StoriesCarousel/StoriesCarousel";
import AppBanner from "../AppBanner/AppBanner";
import Carousel from "react-gallery-carousel";
import { ProgressiveImage } from "../ProgressiveImage/ProgressiveImage";

// Hooks
import useTheme from "../ThemeProvider/useTheme/useTheme";

// Icons
import ArrowRightIcon from "../../icons/ArrowRightIcon";
import CloseIcon from "../../icons/CloseIcon";
import BlockerIcon from "../../icons/BlockerIcon";
import CheckNiceIcon from "../../icons/CheckNiceIcon";

// Utils
import { clientWidth } from "../../utils";
import { modalStyles } from "../../constants";

// Images
import Lotus from "./Lotus";
import AppleAppStore from "./AppleAppStore";
import HeartIcon from "./HeartIcon";
import HighQualityIcon from "./HighQualityIcon";
import LocationIcon from "./LocationIcon";

import OneOnOneSessionsIcon from "./OneOnOneSessionsIcon";
import CaretDownIcon from "../../icons/CaretDownIcon";
import HowItWorks1 from "./HowItWorks1";
import HowItWorks2 from "./HowItWorks2";
import HowItWorks3 from "./HowItWorks3";
import HowItWorks4 from "./HowItWorks4";

import ourCoaches from "./our-coaches.jpg";
import ourCoaches2x from "./our-coaches@2x.jpg";

import trubeIsLifestyle from "./trube-is-lifestyle.jpg";
import trubeIsLifestyle2x from "./trube-is-lifestyle@2x.jpg";
import trubeIsLifestyle3x from "./trube-is-lifestyle@3x.jpg";
import trubeIsLifestyleMobile from "./trube-is-lifestyle-mobile.jpg";
import trubeIsLifestyleMobile2x from "./trube-is-lifestyle-mobile@2x.jpg";

import trubeIsLifestyleMobile3x from "./trube-is-lifestyle-mobile@3x.jpg";
import ourCoachesMobile from "../../images/homepage-our-coaches-mobile.png";

import ourCoachesMobile2x from "../../images/homepage-our-coaches-mobile@2x.png";
import disciplineCardPt from "./discipline-card-pt.jpg";
import disciplineCardPt2x from "./discipline-card-pt@2x.jpg";
import disciplineCardPt3x from "./discipline-card-pt@3x.jpg";
import disciplineCardBoxing from "./discipline-card-boxing.jpg";
import disciplineCardBoxing2x from "./discipline-card-boxing@2x.jpg";
import disciplineCardBoxing3x from "./discipline-card-boxing@3x.jpg";
import disciplineCardYoga from "./discipline-card-yoga.jpg";
import disciplineCardYoga2x from "./discipline-card-yoga@2x.jpg";
import disciplineCardYoga3x from "./discipline-card-yoga@3x.jpg";
import disciplineCardPilates from "./discipline-card-pilates.jpg";
import disciplineCardPilates2x from "./discipline-card-pilates@2x.jpg";
import disciplineCardPilates3x from "./discipline-card-pilates@3x.jpg";
import disciplineCardPrePostNatal from "./discipline-card-prepost-natal.jpg";
import disciplineCardPrePostNatal2x from "./discipline-card-prepost-natal@2x.jpg";
import disciplineCardPrePostNatal3x from "./discipline-card-prepost-natal@3x.jpg";
import disciplineCardStretching from "./discipline-card-stretching.jpg";
import disciplineCardStretching2x from "./discipline-card-stretching@2x.jpg";
import disciplineCardStretching3x from "./discipline-card-stretching@3x.jpg";
import disciplineCardRunning from "./discipline-card-running.jpg";
import disciplineCardRunning2x from "./discipline-card-running@2x.jpg";

import disciplineCardRunning3x from "./discipline-card-running@3x.jpg";
import accessImg from "./access.jpg";
import access2xImg from "./access@2x.jpg";

import access3xImg from "./access@3x.jpg";
import connectImg from "./connect.jpg";
import connect2xImg from "./connect@2x.jpg";

import connect3xImg from "./connect@3x.jpg";
import trainImg from "./train.jpg";
import train2xImg from "./train@2x.jpg";

import train3xImg from "./train@3x.jpg";
import progressImg from "./progress.jpg";
import progress2xImg from "./progress@2x.jpg";
import progress3xImg from "./progress@3x.jpg";

import lifestyleCollectiveLogo from "./lifestyle-collective.png";
import lifestyleCollectiveLogo2x from "./lifestyle-collective@2x.png";
import lifestyleCollectiveLogo3x from "./lifestyle-collective@3x.png";

import homeHeroBanner from "./home-hero-banner.jpg";
import homeHeroBanner2x from "./home-hero-banner@2x.jpg";

import homeHeroBannerMobile from "./home-hero-banner-mobile.jpg";
import homeHeroBannerMobile2x from "./home-hero-banner-mobile@2x.jpg";

import TruBe from "./TruBe";

import lifestyle1 from "../LifestyleLP/lifestyle-carousel-slide-1.jpg";
import lifestyle2 from "../LifestyleLP/lifestyle-carousel-slide-2.jpg";
import lifestyle3 from "../LifestyleLP/lifestyle-carousel-slide-3.jpg";
import lifestyle4 from "../LifestyleLP/lifestyle-carousel-slide-4.jpg";
import lifestyle5 from "../LifestyleLP/lifestyle-carousel-slide-5.jpg";
import lifestyle6 from "../LifestyleLP/lifestyle-carousel-slide-6.jpg";
import lifestyle7 from "../LifestyleLP/lifestyle-carousel-slide-7.jpg";
import lifestyle8 from "../LifestyleLP/lifestyle-carousel-slide-8.jpg";
import lifestyle9 from "../LifestyleLP/lifestyle-carousel-slide-9.jpg";
import lifestyle10 from "../LifestyleLP/lifestyle-carousel-slide-10.jpg";

import solutionsEvents from "../UKComponents/CorporatePage/corporate-page-solutions-events.png";
import solutionsEvents2x from "../UKComponents/CorporatePage/corporate-page-solutions-events@2x.png";
import solutionsHotels from "../UKComponents/CorporatePage/corporate-page-solutions-hotels.png";
import solutionsHotels2x from "../UKComponents/CorporatePage/corporate-page-solutions-hotels@2x.png";
import solutionsHospitality from "../UKComponents/CorporatePage/corporate-page-solutions-hospitality.png";
import solutionsHospitality2x from "../UKComponents/CorporatePage/corporate-page-solutions-hospitality@2x.png";
import solutionsCareHomes from "../UKComponents/CorporatePage/corporate-page-solutions-care-homes.png";
import solutionsCareHomes2x from "../UKComponents/CorporatePage/corporate-page-solutions-care-homes@2x.png";
import solutionsCoWorkingSpaces from "../UKComponents/CorporatePage/corporate-page-solutions-co-working-spaces.png";
import solutionsCoWorkingSpaces2x from "../UKComponents/CorporatePage/corporate-page-solutions-co-working-spaces@2x.png";

import logoBandier from "./logo-bandier.png";
import logoBandier2x from "./logo-bandier@2x.png";
import logoBandier3x from "./logo-bandier@3x.png";

import logoPendry from "./logo-pendry.png";
import logoPendry2x from "./logo-pendry@2x.png";
import logoPendry3x from "./logo-pendry@3x.png";

import logoCymbiotika from "./logo-cymbiotika.png";
import logoCymbiotika2x from "./logo-cymbiotika@2x.png";
import logoCymbiotika3x from "./logo-cymbiotika@3x.png";

import logoNeueHouse from "./logo-neuehouse.png";
import logoNeueHouse2x from "./logo-neuehouse@2x.png";
import logoNeueHouse3x from "./logo-neuehouse@3x.png";

import logoOnRun from "./logo-on-run.png";
import logoOnRun2x from "./logo-on-run@2x.png";
import logoOnRun3x from "./logo-on-run@3x.png";

import logoSoho from "./logo-soho.png";
import logoSoho2x from "./logo-soho@2x.png";
import logoSoho3x from "./logo-soho@3x.png";

import logoSpringPlace from "./logo-spring-place.png";
import logoSpringPlace2x from "./logo-spring-place@2x.png";
import logoSpringPlace3x from "./logo-spring-place@3x.png";

import logoWme from "./logo-wme.png";
import logoWme2x from "./logo-wme@2x.png";
import logoWme3x from "./logo-wme@3x.png";

const galleryImages = [
  lifestyle1,
  lifestyle2,
  lifestyle3,
  lifestyle4,
  lifestyle5,
  lifestyle6,
  lifestyle7,
  lifestyle8,
  lifestyle9,
  lifestyle10,
];

const partnerLogos = [
  {
    src: logoBandier,
    src2x: logoBandier2x,
    src3x: logoBandier3x,
    alt: "Bandier",
  },
  { src: logoPendry, src2x: logoPendry2x, src3x: logoPendry3x, alt: "Pendry" },
  {
    src: logoCymbiotika,
    src2x: logoCymbiotika2x,
    src3x: logoCymbiotika3x,
    alt: "Cymbiotika",
  },
  {
    src: logoNeueHouse,
    src2x: logoNeueHouse2x,
    src3x: logoNeueHouse3x,
    alt: "NeueHouse",
  },
  { src: logoOnRun, src2x: logoOnRun2x, src3x: logoOnRun3x, alt: "On Run" },
  { src: logoSoho, src2x: logoSoho2x, src3x: logoSoho3x, alt: "SOHO" },
  {
    src: logoSpringPlace,
    src2x: logoSpringPlace2x,
    src3x: logoSpringPlace3x,
    alt: "Spring Place",
  },
  { src: logoWme, src2x: logoWme2x, src3x: logoWme3x, alt: "WME" },
];

const corporateSolutions = [
  {
    src: solutionsHotels,
    src2x: solutionsHotels2x,
    alt: "Hotels",
    text: "Corporate",
  },
  {
    src: solutionsHospitality,
    src2x: solutionsHospitality2x,
    alt: "Hospitality",
    text: "Hospitality",
  },
  {
    src: solutionsCoWorkingSpaces,
    src2x: solutionsCoWorkingSpaces2x,
    alt: "Co-Working Spaces",
    text: "Co-Working Spaces",
    multiLine: true,
  },
  {
    src: solutionsCareHomes,
    src2x: solutionsCareHomes2x,
    alt: "Care Homes",
    text: "Care Homes",
  },
  {
    src: solutionsEvents,
    src2x: solutionsEvents2x,
    alt: "Events",
    text: "Events",
  },
];

const sliderResponsiveSettings = [
  {
    breakpoint: 640,
    settings: { slidesToShow: 1, centerMode: true, centerPadding: "32px" },
  },
  {
    breakpoint: 960,
    settings: {
      slidesToShow: 2,
      centerMode: true,
      centerPadding: "50px",
      initialSlide: 1,
    },
  },
  {
    breakpoint: 1180,
    settings: {
      slidesToShow: 3,
      centerMode: true,
      centerPadding: "100px",
      initialSlide: 2,
    },
  },
  {
    breakpoint: 1400,
    settings: {
      slidesToShow: 3,
      slidesToScroll: 2,
      centerMode: true,
      centerPadding: "167px",
      initialSlide: 2,
    },
  },
  {
    breakpoint: 1600,
    settings: {
      slidesToShow: 3,
      slidesToScroll: 2,
      centerMode: true,
      centerPadding: "167px",
      initialSlide: 2,
    },
  },
  {
    breakpoint: 2560,
    settings: {
      slidesToShow: 5,
      slidesToScroll: 2,
      centerMode: true,
      centerPadding: "167px",
      initialSlide: 5,
    },
  },
  {
    breakpoint: 5000,
    settings: {
      slidesToShow: 5,
      slidesToScroll: 2,
      centerMode: true,
      centerPadding: "167px",
      initialSlide: 5,
    },
  },
  { breakpoint: 10000, settings: "unslick" },
];

const heroSliderResponsiveSettings = [
  {
    breakpoint: 640,
    settings: {
      slidesToShow: 1,
      // centerMode: true,
      // centerPadding: "8px",
      initialSlide: 0,
    },
  },
  {
    breakpoint: 960,
    settings: {
      slidesToShow: 1,
      // centerMode: true,
      // centerPadding: "8px",
      initialSlide: 1,
    },
  },
  {
    breakpoint: 1280,
    settings: {
      slidesToShow: 1,
      // centerMode: true,
      // centerPadding: "8px",
      initialSlide: 0,
    },
  },
  {
    breakpoint: 1400,
    settings: {
      slidesToShow: 1,
      slidesToScroll: 1,
      // centerMode: true,
      // centerPadding: "8px",
      initialSlide: 0,
    },
  },
  {
    breakpoint: 1600,
    settings: {
      slidesToShow: 1,
      slidesToScroll: 1,
      // centerMode: true,
      // centerPadding: "8px",
      initialSlide: 0,
    },
  },
  {
    breakpoint: 1920,
    settings: {
      slidesToShow: 1,
      slidesToScroll: 1,
      // centerMode: true,
      // centerPadding: "8px",
      initialSlide: 0,
    },
  },
  {
    breakpoint: 2560,
    settings: {
      slidesToShow: 1,
      slidesToScroll: 1,
      // centerMode: true,
      // centerPadding: "8px",
      initialSlide: 0,
    },
  },
  {
    breakpoint: 5000,
    settings: {
      slidesToShow: 1,
      slidesToScroll: 1,
      // centerMode: true,
      // centerPadding: "8px",
      initialSlide: 0,
    },
  },
  { breakpoint: 10000, settings: "unslick" },
];

export default function Homepage() {
  const location = useLocation();

  const { theme } = useTheme();
  const { region, city } = useRegion();

  const {
    user,
    hasTriedToAutoLogin,
    bookingsWithoutRating,
    referralCode,
    // gyms: gymsData,
    startedEvents,
    upcomingEvents,
    loadBookingsWithoutRating,
    applyReferral,
    setPromoCode,
    setReferralCode,
    // loadSpecialisations,
    // loadAvailablePartnersBySpecialisation,
    loadGyms,
    loadUpcomingEvents,
    clearUpcomingEventsLoaded,
    isFetching,
  } = useAppState();

  const [readMore, setReadMore] = useState(false);
  const [isAskConciergeModalOpen, setIsAskConciergeModalOpen] = useState(false);
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const [isGalleryCarouselModalOpen, setIsGalleryCarouselModalOpen] =
    useState(null);
  const [infoModalTitle, setInfoModalTitle] = useState("");
  const [infoModalMsg, setInfoModalMsg] = useState("");

  const [autoPlay, setAutoPlay] = useState(true);
  const [galleryInitialSlide, setGalleryInitialSlide] = useState(0);
  const [selectedCity, setSelectedCity] = useState(user ? user.city : city);

  const heroRef = React.useRef(null);
  const infoModalResolveRef = useRef(null);

  const questionsSectionRef = useRef();
  const sliderRef = useRef();

  const firstElementRef = useRef(null);

  const handleScrollIndicatorClick = React.useCallback(() => {
    firstElementRef.current.scrollIntoView({ behavior: "smooth" });
  }, []);

  const isUserLoggedIn = !!(user && user.id);

  React.useEffect(() => {
    if (!hasTriedToAutoLogin) {
      return;
    }

    const userCity = user && user.city;

    if (user) {
      loadBookingsWithoutRating(user.id, user.region);
    }

    // loadSpecialisations(
    //   user ? user.region : region /*process.env.REACT_APP_REGION*/
    // );
    //
    // loadSpecialisations(
    //   user ? user.region : region /*process.env.REACT_APP_REGION*/
    // ).then((res) => {
    //   loadPartnersBySpecialisation(
    //     res.data && res.data.length ? res.data[0].value : "ENDURANCE",
    //     user // ? user.region : process.env.REACT_APP_REGION
    //   ); // Will load coaches by "ENDURANCE" specialisation
    // });

    loadGyms({
      withLogos: true,
      region: user ? user.region : region /*process.env.REACT_APP_REGION*/,
    });

    // запрашиваем только когда есть город?
    // выполняется на разлогаут!!! и не только
    // незалогиненным 2 раза!
    if (userCity || selectedCity) {
      loadUpcomingEvents(
        user && user.id,
        "UPCOMING,STARTED,UPCOMING_FULL",
        { sort: "start", page: 0, size: 8 },
        user ? user.region : "US",
        userCity || selectedCity
      );
    }
  }, [user, hasTriedToAutoLogin]);

  React.useEffect(() => {
    var query = new URLSearchParams(location.search);
    var promoCode = query.get("promocode");
    var referralCode = query.get("referral");

    if (referralCode) {
      Cookies.set("referral", referralCode, { path: "/" });
    } else {
      // Remember referral code to survive page reloads (for Apple Login for example)
      referralCode = Cookies.get("referral");
    }

    if (promoCode) {
      Cookies.set("promocode", promoCode, { path: "/" });
    } else {
      // For Apple Sign-in
      promoCode = Cookies.get("promocode");
    }

    if (promoCode) {
      setPromoCode(promoCode);
    }

    if (referralCode) {
      setReferralCode(referralCode);
    }
  }, [location]);

  const openGalleryCarouselModal = (index) => {
    setIsGalleryCarouselModalOpen(true);
    setGalleryInitialSlide(index);
  };

  const closeGalleryCarouselModal = () => {
    setIsGalleryCarouselModalOpen(false);
  };

  const openInfoModal = useCallback((msg, title = "Sorry") => {
    return new Promise((resolve, reject) => {
      infoModalResolveRef.current = resolve;

      setIsInfoModalOpen(true);
      setInfoModalTitle(title);
      setInfoModalMsg(msg);
    });
  }, []);

  const closeInfoModal = useCallback(() => {
    infoModalResolveRef.current();
    infoModalResolveRef.current = null;

    setIsInfoModalOpen(false);
  }, []);

  React.useEffect(() => {
    if (user && referralCode) {
      applyReferral(user.id, referralCode)
        .then((res) => {
          // Referral code is valid
          // openInfoModal(
          //   `You received ${res.data.symbol}${res.data.receiverReward} referral credits.`,
          //   "Referral Bonus Applied"
          // );
        })
        .catch((res) => {
          openInfoModal(res.message, "Link Error");
        })
        .finally(() => {
          // Regardless the referral code is valid or not we don't need it anymore
          // (so it won't be applied on re-login for example)
          setReferralCode("");

          Cookies.remove("referral");
        });
    }
  }, [user, referralCode]);

  const handleReadMoreClick = useCallback(() => {
    setReadMore(true);
  }, []);

  const handleSlideSwipe = useCallback(
    (oldIndex, newIndex) => {
      if (autoPlay) {
        setAutoPlay(false);
      }
    },
    [autoPlay]
  );

  const handleRegionClick = useCallback(() => {
    //openRegionSelectModal();
    // setIsRegionSelectModalOpen(true);
  }, []);

  const handleCityChange = useCallback(
    (newCity) => {
      if (selectedCity === newCity) {
        return;
      }

      const userCity = user && user.city;

      setSelectedCity(newCity);
      loadUpcomingEvents(
        user && user.id,
        "UPCOMING,STARTED,UPCOMING_FULL",
        { sort: "start", page: 0, size: 8 },
        user ? user.region : "US",
        // user ? user.city : newCity
        userCity || newCity
      );
    },
    [user, region, selectedCity, loadUpcomingEvents]
  );

  const futureEvents =
    startedEvents && startedEvents.length > 0
      ? [].concat(startedEvents, upcomingEvents)
      : upcomingEvents;

  return (
    <>
      <ScrollToTopOnMount />
      <Title>TruBe: Top Fitness & Wellbeing Classes & Personal Trainers</Title>
      <Meta
        name="description"
        content="TruBe offers the best fitness & wellbeing group classes and one-to-one trainers for Pilates, Yoga, Personal Training, HIIT, Running, and more. Tailored to you!"
      />
      <Header
        showRegionSelect
        isLoggedIn={isUserLoggedIn}
        heroRef={heroRef}
        region={region}
        returnTo="/"
        onFaqClick={() =>
          questionsSectionRef.current.scrollIntoView({ behavior: "smooth" })
        }
        onChooseRegionClick={handleRegionClick}
        onUpcomingClassesLinkClick={clearUpcomingEventsLoaded}
      />

      <Slider
        ref={sliderRef}
        className={`home-hero-carousel tbk-max-h-screen_ tbk-min-h-[690px]_ tbk-h-[690px] tbk-overflow-hidden tbk-bg-[#777777] !tbk-pt-0 lg:tbk-mb-7`}
        dots={true}
        appendDots={(dots) => (
          <ul onClick={handleSlideSwipe}>
            {dots.map((item, index) => {
              return (
                <li key={index} className={item.props.className}>
                  {item.props.children}
                </li>
              );
            })}
          </ul>
        )}
        arrows={clientWidth() > 960}
        autoplay={autoPlay}
        autoplaySpeed={8000}
        pauseOnFocus
        pauseOnDotsHover
        pauseOnHover
        infinite={true}
        initialSlide={0}
        speed={500}
        slidesToShow={1}
        slidesToScroll={1}
        useTransform={false}
        //adaptiveHeight
        nextArrow={
          <SliderNextButton
            buttonClassName="tbk-w-9 tbk-h-9 tbk-text-basic-white tbk-backdrop-blur-xl hover:tbk-bg-basic-white hover:tbk-text-basic-trueblack"
            onBtnClick={handleSlideSwipe}
          />
        }
        prevArrow={
          <SliderPrevButton
            buttonClassName="tbk-w-9 tbk-h-9 tbk-text-basic-white tbk-backdrop-blur-xl hover:tbk-bg-basic-white hover:tbk-text-basic-trueblack"
            onBtnClick={handleSlideSwipe}
          />
        }
        onSwipe={handleSlideSwipe}
        responsive={heroSliderResponsiveSettings}
      >
        <div id="slide-1" className="slide">
          <div
            className="content slide-bg js-progressive-image tbk-relative tbk-w-full tbk-bg-[#54574C]"
            // style={{ width: "100%", height: "100%" }}
          >
            <div
              ref={heroRef}
              className="tbk-relative tbk-flex tbk-h-[690px] tbk-items-end lg:tbk-items-start lg:tbk-pt-7"
            >
              <div className="tbk-absolute tbk-bottom-0 tbk-left-0 tbk-right-0 tbk-top-0 tbk-z-1 tbk-flex tbk-h-85 tbk-w-full tbk-items-end tbk-overflow-hidden md:tbk-h-full lg:tbk-min-h-[690px]">
                <video
                  autoPlay
                  loop
                  className="tbk-absolute tbk-m-auto tbk-h-full tbk-w-full tbk-bg-cover tbk-bg-center tbk-object-cover"
                  style={{
                    backgroundImage:
                      "url(https://assets-global.website-files.com/60785b43631c8625d8c10c7e/661d47f9e730db6130053f3f_Lifestyle-poster-00001.jpg)",
                  }}
                  muted
                  playsInline
                >
                  <source src="https://assets-global.website-files.com/60785b43631c8625d8c10c7e/661d47f9e730db6130053f3f_Lifestyle-transcode.mp4" />
                  <source src="https://assets-global.website-files.com/60785b43631c8625d8c10c7e/661d47f9e730db6130053f3f_Lifestyle-transcode.webm" />
                </video>
                {/*<div className="tbk-absolute tbk-h-full tbk-w-full tbk-bg-gradient-to-r tbk-from-basic-trueblack/60 tbk-to-basic-trueblack/60"/>*/}
                <div className="tbk-absolute tbk-bottom-0 tbk-left-0 tbk-right-0 tbk-top-0 tbk-bg-[#C3A48D]/20" />
                <div className="tbk-absolute tbk-bottom-0 tbk-left-0 tbk-right-0 tbk-top-0 tbk-bg-gradient-to-b tbk-from-[#8BABC1]/0 tbk-from-30% tbk-to-[#54574C] tbk-to-100% md:tbk-hidden" />
              </div>

              <div className="tbk-container tbk-z-1 tbk-mx-auto tbk-px-2 lg:tbk-px-8">
                {/*<div className="tbk-relative tbk-h-full md:tbk-grid_ md:tbk-grid-cols-lifestyle-cols-layout  lg:tbk-pb-8 lg:tbk-pt-6">*/}
                <div className="tbk-mt-2_ tbk-h-full_ tbk-mb-2_ lg:tbk-mt-7_ tbk-flex tbk-grow tbk-flex-col tbk-items-center tbk-justify-end tbk-pb-8 md:tbk-justify-start md:tbk-pb-26 lg:tbk-pt-7">
                  <div className="tbk-mb-3 tbk-flex tbk-flex-col tbk-items-center tbk-justify-center md:tbk-justify-start">
                    <TruBe width={105} height={32} className="tbk-mb-1" />
                    <img
                      className="md:tbk-h-24"
                      src={lifestyleCollectiveLogo}
                      srcSet={`${lifestyleCollectiveLogo} 1x, ${lifestyleCollectiveLogo2x} 2x, ${lifestyleCollectiveLogo3x} 3x`}
                      alt="Lifestyle Collective"
                    />
                  </div>

                  <div className="tbk-overflow-hidden tbk-pb-0 tbk-pt-2 tbk-will-change-background md:tbk-w-100 md:tbk-rounded-2xl md:tbk-px-3 md:tbk-pb-3 md:tbk-backdrop-blur-xl lg:tbk-pt-3">
                    {/*<div className="tbk-mb-3 tbk-flex tbk-flex-col tbk-items-center tbk-justify-center md:tbk-justify-center">*/}
                    {/*</div>*/}

                    <p className="tbk-text-main tbk-mb-2 tbk-text-basic-white">
                      Join the TruBe Lifestyle Collective for curated group
                      fitness & wellness experiences led by top coaches. Access
                      up to three events monthly for $54/month. Immerse yourself
                      in a supportive environment, connect with like-minded
                      individuals, and prioritize your well-being
                    </p>
                    <div className="tbk-text-center md:tbk-text-left">
                      <Link to={"/lifestyle"}>
                        <Button
                          size="md"
                          variant="secondary"
                          className="tbk-inline-flex tbk-w-full"
                          endIcon={<ArrowRightIcon />}
                        >
                          Learn More
                        </Button>
                      </Link>
                    </div>
                  </div>
                </div>

                <ScrollingIndicator
                  className={
                    "tbk-absolute tbk-bottom-1 tbk-right-2 md:tbk-absolute md:tbk-bottom-5 md:tbk-bottom-6 md:tbk-left-1/2 md:tbk-right-auto md:tbk--translate-x-1/2"
                  }
                  onClick={handleScrollIndicatorClick}
                />
              </div>
            </div>
          </div>
        </div>

        <div id="slide-2" className="slide">
          <div
            className="content slide-bg js-progressive-image tbk-relative tbk-w-full"
            // style={{ width: "100%", height: "100%" }}
          >
            <div
              //ref={heroRef}
              className="tbk-relative tbk-flex tbk-items-center lg:tbk-pt-7"
            >
              <div className="md:tbk-h-full_ tbk-absolute tbk-bottom-0 tbk-left-0 tbk-right-0 tbk-top-0 tbk-z-1 tbk-flex tbk-h-[690px] tbk-w-full tbk-items-end tbk-overflow-hidden">
                <picture className="tbk-h-full tbk-grow">
                  <source
                    media="(min-width: 768px)"
                    srcSet={`${homeHeroBanner} 1x, ${homeHeroBanner2x} 2x`}
                  />
                  <source
                    srcSet={`${homeHeroBannerMobile} 1x, ${homeHeroBannerMobile2x} 2x`}
                  />
                  <img
                    className="tbk-h-full tbk-w-full tbk-object-cover md:tbk-h-full"
                    src={homeHeroBanner}
                    alt="Discover Your TruBest"
                  />
                </picture>

                {/*<div className="tbk-absolute tbk-right-0 tbk-bottom-0 tbk-top-0 tbk-left-0 lg:tbk-bg-gradient-to-l lg:tbk-from-[#7891AE]/0 lg:tbk-to-[#577490]" />*/}
                {/*<div className="tbk-from-55% tbk-to-100% tbk-absolute tbk-right-0 tbk-bottom-0 tbk-top-0 tbk-left-0 tbk-bg-gradient-to-b tbk-from-[#8BABC1]/0 tbk-to-[#8BABC1] md:tbk-hidden" />*/}
              </div>

              <div className="tbk-container tbk-z-1 tbk-mx-auto tbk-px-2 lg:tbk-px-8">
                <div className="tbk-relative tbk-h-full tbk-pb-8 tbk-pt-2 lg:tbk-grid lg:tbk-grid-cols-2 lg:tbk-items-end lg:tbk-pb-8 lg:tbk-pt-6">
                  <div className="">
                    <div className="tbk-text-epic-bold lg:tbk-text-extra-bold tbk-mb-4 tbk-mt-7 tbk-text-basic-white lg:tbk-mt-0 lg:tbk-whitespace-nowrap">
                      Discover
                      <br /> Your
                      <br />
                      TruBest
                    </div>
                    <p className="tbk-text-h3-subtitle lg:tbk-text-title-medium tbk-mb-4 !tbk-leading-unified tbk-text-basic-white lg:tbk-hidden">
                      On-demand, in-person, coaches, trainers, and wellness
                      professionals in the comfort of your own space, at your
                      convenience.
                    </p>
                    <p className="tbk-text-h3-subtitle lg:tbk-text-title-medium tbk-mb-4 tbk-hidden tbk-text-basic-white lg:tbk-block">
                      In-person expert coaches and wellness professionals in the
                      comfort of your own space, at your convenience.
                    </p>
                    <Link
                      to={
                        isUserLoggedIn || true
                          ? "/partners"
                          : "/become-a-member"
                      }
                    >
                      <Button
                        variant={"secondary"}
                        className="tbk-mb-4 tbk-inline-block tbk-w-full lg:tbk-mb-0 lg:tbk-w-68"
                        endIcon={<ArrowRightIcon />}
                      >
                        {isUserLoggedIn || true ? "View Coaches" : "Join Now"}
                      </Button>
                    </Link>
                  </div>
                  <div className="tbk-flex tbk-justify-center lg:tbk-justify-end">
                    <a
                      href="https://apps.apple.com/us/app/trube/id6449003944"
                      className=""
                    >
                      <AppleAppStore className="" width={165} height={48} />
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Slider>

      <div
        ref={firstElementRef}
        className="tbk-flex tbk-flex-col tbk-items-center tbk-justify-center tbk-pb-7 tbk-pt-7 lg:tbk-pb-9 lg:tbk-pt-9"
      >
        <div className="tbk-container tbk-px-2 lg:tbk-px-8">
          <div className="lg:tbk-mb-7 lg:tbk-grid lg:tbk-grid-cols-2 lg:tbk-gap-8">
            <div className="">
              <h2 className="tbk-text-title-bold lg:tbk-text-h2-black lg:!tbk-leading-none_ tbk-mb-2 !tbk-leading-tight tbk-text-primary lg:tbk-mb-2">
                Connect, Inspire, Flourish: <br className="lg:tbk-hidden" />
                Join TruBe's Wellness <br className="lg:tbk-hidden" />
                Community
              </h2>
              <p className="tbk-text-main tbk-mb-[48px] tbk-text-primary lg:tbk-mb-0">
                Step into TruBe’s lifestyle collective and gain access to
                fitness and well-being classes that go beyond the mundane
                workout. Join our community and discover weekly classes led by
                the best coaches of their chosen disciplines, including HIIT,
                Boxing, Pilates, Yoga, Meditation, Sound Healing, and more.
                <br />
                <br />
                Held at exclusive venues, each class provides additional service
                offerings from our brand partners, whilst immersing you in a
                supportive environment, connecting you with like-minded
                individuals, and forging meaningful relationships as you improve
                your well-being.
              </p>
              {/*<Link to={isUserLoggedIn ? "/partners" : "/become-a-member"}>*/}
              {/*  <Button*/}
              {/*    size="md"*/}
              {/*    className="tbk-mx-auto tbk-w-64 lg:tbk-mx-0"*/}
              {/*    endIcon={<ArrowRightIcon />}*/}
              {/*  >*/}
              {/*    {isUserLoggedIn ? "Start Now" : "Become a Member"}*/}
              {/*  </Button>*/}
              {/*</Link>*/}
            </div>
            <div>
              <div className="lg:tbk-mb-4_ tbk-grid tbk-grid-cols-2 tbk-justify-between tbk-gap-2 lg:tbk-justify-start">
                <div className="tbk-rounded-2xl tbk-border tbk-border-solid tbk-border-blue-grey-light tbk-px-2 tbk-pb-3 tbk-pt-2 tbk-text-center tbk-text-primary">
                  <HeartIcon
                    size={clientWidth() < 960 ? 40 : null}
                    className="tbk-mb-1 tbk-inline-block"
                  />
                  <div className="tbk-text-small lg:tbk-text-h3-subtitle !tbk-font-semibold md:tbk-px-1 lg:!tbk-font-medium">
                    Gifts & Discounts from
                    <br />
                    Fitness Brands
                  </div>
                </div>
                <div className="tbk-rounded-2xl tbk-border tbk-border-solid tbk-border-blue-grey-light tbk-px-2 tbk-pb-3 tbk-pt-2 tbk-text-center tbk-text-primary">
                  <HighQualityIcon
                    size={clientWidth() < 960 ? 40 : null}
                    className="tbk-mb-1 tbk-inline-block"
                  />
                  <div className="tbk-text-small lg:tbk-text-h3-subtitle !tbk-font-semibold md:tbk-px-1 lg:!tbk-font-medium">
                    Curated by Certified Specialists
                  </div>
                </div>
                <div className="tbk-rounded-2xl tbk-border tbk-border-solid tbk-border-blue-grey-light tbk-px-2 tbk-pb-3 tbk-pt-2 tbk-text-center tbk-text-primary xl:tbk-block ">
                  <LocationIcon
                    size={clientWidth() < 960 ? 40 : null}
                    className="tbk-mb-1 tbk-inline-block"
                  />
                  <div className="tbk-text-small lg:tbk-text-h3-subtitle !tbk-font-semibold md:tbk-px-1 lg:!tbk-font-medium">
                    Immersive Classes at Exclusive Venues
                  </div>
                </div>
                <div className="tbk-rounded-2xl tbk-border tbk-border-solid tbk-border-blue-grey-light tbk-px-2 tbk-pb-3 tbk-pt-2 tbk-text-center tbk-text-primary">
                  <OneOnOneSessionsIcon
                    width={clientWidth() < 960 ? 54 : null}
                    height={clientWidth() < 960 ? 40 : null}
                    className="tbk-mb-1 tbk-inline-block"
                  />
                  <div className="tbk-text-small lg:tbk-text-h3-subtitle !tbk-font-semibold md:tbk-px-1 lg:!tbk-font-medium">
                    Variety
                    <br />
                    of Disciplines
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="user-select-none tbk-w-full tbk-max-w-full tbk-overflow-hidden">
        <div className="tbk-flex tbk-h-full tbk-flex-col tbk-overflow-hidden">
          <div className="tb-moving-bar tbk-relative tbk-flex tbk-h-76 tbk-max-w-full  tbk-overflow-hidden lg:tbk-h-100">
            <MovingBar
              className="tbk-absolute_ "
              images={galleryImages.map((v) => ({ url: v }))}
              imgClassName="tbk-h-76 tbk-w-76 tbk-rounded-2xl lg:tbk-h-100 lg:tbk-w-100"
              onClick={openGalleryCarouselModal}
            />
            <MovingBar
              className="tbk-absolute_ tbk-right-0 "
              images={galleryImages.map((v) => ({ url: v }))}
              imgClassName="tbk-h-76 tbk-w-76 tbk-rounded-2xl lg:tbk-h-100 lg:tbk-w-100"
              onClick={openGalleryCarouselModal}
            />
          </div>
        </div>
      </div>

      <div className="tbk-flex tbk-flex-col tbk-items-center tbk-justify-center tbk-pb-4 tbk-pt-4 lg:tbk-pb-7 lg:tbk-pt-7">
        {/*<div className="tbk-container tbk-px-3 lg:tbk-px-8">*/}
        {/*  */}
        {/*</div>*/}
        <div className="tbk-mb-3_ lg:tbk-mb-5_ tbk-relative tbk-text-center">
          <Link
            to={
              isUserLoggedIn
                ? "/lifestyle"
                : {
                    pathname: `/become-a-member`,
                    search: `?return_to=${encodeURIComponent(
                      "/checkout?membershipId=1"
                    )}`,
                    state: { from: "/lifestyle" },
                  }
            }
            className="tbk-inline-block"
          >
            <Button
              //size={"md"}
              className="tbk-w-68"
              endIcon={<ArrowRightIcon />}
              onClick={clearUpcomingEventsLoaded}
            >
              {isUserLoggedIn ? "Learn More" : "Become a Member"}
            </Button>
          </Link>
        </div>
      </div>

      <div className="tbk-flex tbk-flex-col tbk-items-center tbk-justify-center tbk-pb-4 tbk-pt-7 lg:tbk-pb-5 lg:tbk-pt-9">
        <div className="tbk-container tbk-px-3 lg:tbk-px-8">
          <HeadlineWithSeparator
            component="h3"
            titleClass="tbk-bg-basic-white tbk-text-blue-grey tbk-text-h3-subtitle lg:tbk-text-title-bold"
            className="tbk-mb-3 lg:tbk-mb-5"
            separatorClass={"tbk--mt-1.5 lg:tbk--mt-2"}
          >
            Upcoming Classes
          </HeadlineWithSeparator>

          {region === "US" && (
            <CityPickerButton
              className="tbk-mb-4 tbk-mt-4"
              city={selectedCity}
              onCityChange={handleCityChange}
            />
          )}
          <div className="tbk-mb-6 ">
            <UpcomingExperiencesSlider
              showArrows={clientWidth() > theme.responsive.breakpoints.lg}
              className="tbk-pb-3"
              itemComponent={EventCard}
              itemProps={{
                to: !isUserLoggedIn &&
                  false && {
                    pathname: `/become-a-member`,
                    search: "?return_to=/checkout?membershipId=1",
                    state: { from: location.pathname },
                  },
                imgClassName: "tbk-h-48 lg:tbk-h-76",
                showRsvpCta: true,
                isUserLoggedIn: isUserLoggedIn,
                onRsvpCtaClick: clearUpcomingEventsLoaded,
              }}
              futureEvents={futureEvents}
              isFetching={isFetching}
            />
          </div>
        </div>
        {/*<div className="tbk-mb-3_ lg:tbk-mb-5_ tbk-relative tbk-text-center">*/}
        {/*  <Link to={`/events/upcoming`} className="tbk-inline-block">*/}
        {/*    <Button*/}
        {/*      size={"md"}*/}
        {/*      className="tbk-w-64"*/}
        {/*      endIcon={<ArrowRightIcon />}*/}
        {/*      onClick={clearUpcomingEventsLoaded}*/}
        {/*    >*/}
        {/*      View All Upcoming*/}
        {/*    </Button>*/}
        {/*  </Link>*/}
        {/*</div>*/}
      </div>

      <div className="tbk-pb-7 tbk-pt-7 lg:tbk-pb-7 lg:tbk-pt-7">
        <LogoBar />
        <div className="tbk-container tbk-mx-auto tbk-px-2 tbk-text-center lg:tbk-px-8">
          <Link to={"/events"} className="tbk-inline-block">
            <Button
              size="md"
              className="tbk-mt-3 tbk-w-64"
              endIcon={<ArrowRightIcon />}
            >
              Reserve Your Spot
            </Button>
          </Link>
        </div>
      </div>

      <div className="tbk-flex tbk-flex-col tbk-items-center tbk-justify-center tbk-pb-7 tbk-pt-7 lg:tbk-pb-9 lg:tbk-pt-7">
        <div className="tbk-container tbk-px-2 lg:tbk-px-8">
          <HeadlineWithSeparator
            component="h3"
            textAlign="left"
            titleClass="tbk-bg-basic-white tbk-text-epic-medium lg:tbk-text-epic-bold tbk-text-blue-grey !tbk-leading-none lg:!tbk-leading-tight"
            className="tbk-mb-4 lg:tbk-mb-5"
            separatorClass={"tbk--mt-1.5 lg:tbk--mt-2"}
          >
            Private
            <br />
            1:1 Coaching
          </HeadlineWithSeparator>

          <div className="lg:tbk-grid lg:tbk-grid-cols-2 lg:tbk-gap-8">
            <div className="tbk-mb-4 lg:tbk-mb-0">
              <h2 className="tbk-text-title-bold lg:tbk-text-h2-black lg:!tbk-leading-none_ tbk-mb-2 !tbk-leading-tight tbk-text-primary lg:tbk-mb-4">
                Achieve Your Potential with{" "}
                <span className="tbk-text-coral">Private Classes</span>,
                Specialist Advice, and Tailored Nutrition Guidance
              </h2>

              <Link className="tbk-hidden lg:tbk-block" to={"/partners"}>
                <Button
                  size="md"
                  className="tbk-mx-auto tbk-w-70 lg:tbk-mx-0"
                  endIcon={<ArrowRightIcon />}
                >
                  Find a Coach Near You
                </Button>
              </Link>
            </div>
            <div>
              <p className="tbk-text-main tbk-mb-4 tbk-text-primary lg:tbk-mb-0">
                At TruBe, we empower individuals to achieve their fitness and
                well-being goals by connecting them with the best coaches. By
                providing a personalized, convenient, and effective 1:1 class,
                we enable you to build and maintain a healthier lifestyle for
                the future.
                <br />
                <br />
                Instilled from our London roots, our concierge styled wellness
                approach treats every customer differently. Whether your goal is
                weight loss, build muscle, or improve your strength and
                conditioning – Our coaches will take the time to contact you,
                establish your individual needs, and curate a program to help
                you achieve your goals.
              </p>
              <Link className="lg:tbk-hidden" to={"/partners"}>
                <Button
                  size="md"
                  className="tbk-mx-auto tbk-w-70 lg:tbk-mx-0"
                  endIcon={<ArrowRightIcon />}
                >
                  Find a Coach Near You
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/*      </div>*/}
      {/*      <div />*/}
      {/*    </div>*/}
      {/*  </div>*/}
      {/*</div>*/}

      <Slider
        className="home-disciplines-carousel md:tbk-mt-9_ lg:tbk-mb-7"
        dots={false}
        infinite={true}
        initialSlide={1}
        speed={500}
        slidesToShow={1}
        slidesToScroll={1}
        nextArrow={
          clientWidth() < 960 ? (
            <SliderNextButton />
          ) : (
            <SliderNextButton buttonClassName="tbk-w-9 tbk-h-9" />
          )
        }
        prevArrow={
          clientWidth() < 960 ? (
            <SliderPrevButton />
          ) : (
            <SliderPrevButton buttonClassName="tbk-w-9 tbk-h-9" />
          )
        }
        responsive={sliderResponsiveSettings}
      >
        <DisciplineSlide
          id="slide-1"
          discipline="Kickboxing"
          description="Control & Power"
          imageUrl={disciplineCardBoxing}
          imageUrl2x={disciplineCardBoxing2x}
          imageUrl3x={disciplineCardBoxing3x}
          link="/partners/kickboxing?session_type=Kickboxing"
        />
        <DisciplineSlide
          id="slide-2"
          discipline="Stretching"
          description="Fluidity & Movement"
          imageUrl={disciplineCardStretching}
          imageUrl2x={disciplineCardStretching2x}
          imageUrl3x={disciplineCardStretching3x}
          link="/partners/stretching?session_type=Stretching"
        />
        <DisciplineSlide
          id="slide-3"
          discipline="Personal Training"
          description="Functionality & Body Awareness"
          imageUrl={disciplineCardPt}
          imageUrl2x={disciplineCardPt2x}
          imageUrl3x={disciplineCardPt3x}
          link="/partners/personal-training?session_type=Personal+Training"
        />
        <DisciplineSlide
          id="slide-4"
          discipline="Yoga"
          description="Balance & Mindfulness"
          imageUrl={disciplineCardYoga}
          imageUrl2x={disciplineCardYoga2x}
          imageUrl3x={disciplineCardYoga3x}
          link="/partners/yoga?session_type=Yoga"
        />
        <DisciplineSlide
          id="slide-5"
          discipline="Pilates"
          description="Confidence & Longevity"
          imageUrl={disciplineCardPilates}
          imageUrl2x={disciplineCardPilates2x}
          imageUrl3x={disciplineCardPilates3x}
          link="/partners/pilates?session_type=Pilates"
        />
        <DisciplineSlide
          id="slide-6"
          discipline="Pre/Post Natal"
          description="Perseverance & Uplifting"
          imageUrl={disciplineCardPrePostNatal}
          imageUrl2x={disciplineCardPrePostNatal2x}
          imageUrl3x={disciplineCardPrePostNatal3x}
          link="/partners/prepost-natal?session_type=Pre%2FPost+Natal"
        />
        <DisciplineSlide
          id="slide-7"
          discipline="Running"
          description="Perseverance & Uplifting"
          imageUrl={disciplineCardRunning}
          imageUrl2x={disciplineCardRunning2x}
          imageUrl3x={disciplineCardRunning3x}
          link="/partners/running?session_type=Running"
        />
      </Slider>

      <div className="tbk-flex tbk-hidden tbk-flex-col tbk-items-center tbk-justify-center tbk-pb-7 tbk-pt-32 lg:tbk-pb-8 lg:tbk-pt-9">
        <div className="tbk-container tbk-px-3 lg:tbk-px-2">
          <div className="lg:tbk-gap- lg:tbk-grid lg:tbk-grid-cols-2 lg:tbk-gap-8">
            <div className="tbk-mb-3 lg:tbk-mb-0">
              <h2 className="tbk-text-h2-black lg:tbk-text-epic-medium tbk-mb-2 tbk-text-primary lg:tbk-mb-4">
                Our <span className="tbk-text-coral">Coaches</span>
              </h2>

              {clientWidth() < 960 && !readMore ? (
                <ClampLines
                  text={
                    "Our coaches are driven by a profound commitment to your success. We're not just trainers; we're listeners who genuinely care about your journey. Our purpose is to support you every step of the way, crafting individualized solutions that cater to your unique needs. We go beyond the workout, curating in-person relationships that inspire trust and growth. Together, we lead you to your goals, ensuring that every session is a step closer to your best self. \n \n Our stringent recruitment process brings together a diverse group of coaches with various specializations. By harmonizing their paths with innovation and technology, we ensure that every member of the TruBe community is motivated to find the perfect match to achieve their unique fitness and well-being goals."
                  }
                  id={"our-coaches"}
                  className="tbk-text-main tbk-mb-2 tbk-text-primary"
                  lines={9}
                  ellipsis="..."
                  buttons={false}
                />
              ) : (
                <p className="tbk-text-main tbk-mb-4 tbk-text-primary">
                  Our coaches are driven by a profound commitment to your
                  success. We're not just trainers; we're listeners who
                  genuinely care about your journey. Our purpose is to support
                  you every step of the way, crafting individualized solutions
                  that cater to your unique needs. We go beyond the workout,
                  curating in-person relationships that inspire trust and
                  growth. Together, we lead you to your goals, ensuring that
                  every session is a step closer to your best self.
                  <br />
                  <br />
                  Our stringent recruitment process brings together a diverse
                  group of coaches with various specializations. By harmonizing
                  their paths with innovation and technology, we ensure that
                  every member of the TruBe community is motivated to find the
                  perfect match to achieve their unique fitness and well-being
                  goals.
                </p>
              )}

              {clientWidth() < 960 && !readMore && (
                <div
                  className="tbk-relative tbk-mb-4 tbk-w-full tbk-cursor-pointer tbk-text-center"
                  onClick={handleReadMoreClick}
                >
                  <div className="tbk-text-main tbk-inline-block tbk-inline-flex tbk-items-center tbk-bg-basic-white tbk-px-2 tbk-text-center tbk-text-blue-grey tbk-underline hover:tbk-text-grey-inactive">
                    Read More
                    <CaretDownIcon className="tbk-ml-0.5" />
                  </div>
                  <hr className="tbk--mt-2 tbk-mb-0 tbk-border-blue-grey-light" />
                </div>
              )}
              <div className="tbk-text-center lg:tbk-text-left">
                <Link
                  to={isUserLoggedIn || true ? "/partners" : "/become-a-member"}
                >
                  <Button
                    size={"md"}
                    className="tbk-mb-3 tbk-mt-3 tbk-inline-flex tbk-pl-5 tbk-pr-5 lg:tbk-mb-0 lg:tbk-mt-0"
                    endIcon={<ArrowRightIcon className="tbk-ml-0.5" />}
                  >
                    {isUserLoggedIn || true
                      ? "Explore All Coaches"
                      : "Explore All Coaches"}
                  </Button>
                </Link>
              </div>
            </div>

            <div className="tbk-relative tbk-overflow-hidden tbk-rounded-2xl tbk-bg-home-our-coaches-mobile lg:tbk-h-auto lg:tbk-bg-none">
              <picture>
                <source
                  media="(min-width: 960px)"
                  srcSet={`${ourCoaches} 1x, ${ourCoaches2x} 2x`}
                />
                <source
                  srcSet={`${ourCoachesMobile} 1x, ${ourCoachesMobile2x} 2x`}
                />
                <img
                  className="tbk-relative tbk--z-1 tbk-h-full tbk-w-full tbk-object-cover"
                  role="presentation"
                  src={ourCoaches}
                  alt="Our Coaches"
                />
              </picture>
              <div className="tbk-text-title-bold lg:tbk-text-h2-black tbk-absolute tbk-left-0 tbk-top-0 tbk-p-2 tbk-text-basic-white lg:tbk-p-3">
                Your Trainings guided by professionals with empathy and care.
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="tbk-flex tbk-hidden tbk-flex-col tbk-items-center tbk-justify-center tbk-pb-4 tbk-pb-6 tbk-pt-8 lg:tbk-pb-24">
        <div className="tbk-container tbk-px-2 lg:tbk-px-8">
          <div className="tbk-mb-4 tbk-text-center lg:tbk-mt-8">
            <Lotus className="tbk-inline-block" />
          </div>
          <p className="tbk-text-h3-subtitle lg:tbk-text-epic-medium tbk-text-grey-main lg:tbk-text-center lg:tbk-tracking-tighter">
            Your Journey Starts Here. TruBe brings in-person fitness and
            wellness sessions with certified coaches directly to where you live,
            work, or play, at a time that works for your schedule.
            <br />
            <br />
            Choose from a wide range of disciplines to effortlessly achieve your
            fitness goals with  ease, convenience, and accessibility. TruBe
            empowers you to exceed expectations and become your TruBest.
          </p>
        </div>
      </div>

      <div className="tbk-hidden tbk-pb-5 tbk-pt-5 lg:tbk-pb-8 lg:tbk-pt-8">
        <div className="tbk-container tbk-mx-auto tbk-px-2 lg:tbk-px-34">
          <p className="tbk-text-h3-subtitle lg:tbk-text-bright tbk-text-grey-main lg:tbk-text-center">
            Real lifestyle experiences start with real people and authentic,
            immersive engagement. TruBe is leading the well-being movement by
            rising above existing offerings and catering to people's
            post-pandemic needs, wants and desires, from the comfort of their
            home, office, or personal spaces.
            <br />
            <br />
            Our four well-being pillars are designed to deliver everything our
            Members need to Look Good, Feel Good, and Do Good.
          </p>
        </div>
      </div>

      <div className="tbk-relative tbk-bg-basic-white tbk-pt-4 lg:tbk-mb-0 lg:tbk-pb-2 lg:tbk-pt-7 ">
        <div className="tbk-container tbk-z-1 tbk-mx-auto tbk-px-2 lg:tbk-px-8 ">
          <HeadlineWithSeparator
            component="h3"
            titleClass="tbk-bg-basic-white tbk-text-blue-grey tbk-text-h3-subtitle lg:tbk-text-title-bold"
            className="lg:tbk-mb-5_ tbk-mb-2"
            separatorClass={"tbk-mb-0 tbk--mt-1.5_ lg:tbk--mt-2"}
          >
            TruBe Success Stories
          </HeadlineWithSeparator>
        </div>
      </div>

      <div className="tbk-pb-24_ tbk-bg-basic-white tbk-pb-7 lg:tbk-pb-7">
        <HomeStoriesCarousel />
      </div>

      <section
        id="get-in-touch"
        //ref={getInTouchRef}
        className="cta-form-section tbk-mb-4_ tbk-relative tbk-overflow-hidden tbk-bg-basic-white tbk-pb-7 tbk-pt-7 md:tbk-pb-7 md:tbk-pt-7"
      >
        <div className="tbk-container tbk-mx-auto tbk-px-2 lg:tbk-px-7">
          <HeadlineWithSeparator
            component="h3"
            textAlign="left"
            titleClass="tbk-bg-basic-white tbk-text-epic-medium lg:tbk-text-epic-bold tbk-text-blue-grey !tbk-leading-none lg:!tbk-leading-tight"
            className="tbk-mb-4 lg:tbk-mb-6"
            separatorClass={"tbk--mt-1.5 lg:tbk--mt-2"}
          >
            Corporate
            <br />
            Experiences
          </HeadlineWithSeparator>

          <div className="tbk-mb-[48px] tbk-flex tbk-flex-col tbk-items-start tbk-justify-center lg:tbk-grid-cols-2 lg:tbk-flex-row lg:tbk-items-start lg:tbk-gap-8">
            <div className="tbk--order-1_ md:tbk-w-112_ lg:tbk-order-1_ tbk-mb-4 tbk-w-full lg:tbk-mb-0">
              <p className="tbk-text-main tbk-mb-3">
                TruBe offers tailored group fitness classes for businesses of
                all sizes, designed to enhance employee well-being, boost
                productivity, and foster team cohesion. With years of experience
                in delivering personalized fitness solutions, our team brings
                energy and expertise to each session, ensuring that participants
                of all fitness levels can engage and benefit. These classes not
                only promote physical health but also reduce stress and improve
                mental clarity, making them a valuable investment in a
                healthier, more motivated workforce.
              </p>
              {/*<Link to={"/cymbiotika"}>*/}
              <Button
                size="md"
                variant="secondary"
                className="tbk-w-52_ tbk-inline-flex"
                endIcon={<ArrowRightIcon />}
                onClick={() => setIsAskConciergeModalOpen(true)}
              >
                Learn More about Corporate Experience
              </Button>
              {/*</Link>*/}
            </div>
            <div className="md:tbk-w-112_ tbk-w-full">
              <CorporateContactForm />
            </div>
          </div>

          <div className="tbk-mb-6 tbk-mt-1 tbk-flex tbk-flex-wrap tbk-justify-evenly tbk-gap-3 lg:tbk-justify-between">
            {partnerLogos.map((logo, index) => {
              const imgConfig = [
                { breakpoint: "xs", url: logo.src, url2x: logo.src2x },
                { breakpoint: "md", url: logo.src, url2x: logo.src2x },
                { breakpoint: "lg", url: logo.src, url2x: logo.src2x },
              ];
              return (
                <div
                  key={index}
                  className="tbk-relative tbk-flex tbk-h-6 tbk-shrink-0 tbk-grow tbk-justify-center md:tbk-h-7"
                >
                  {/*<img*/}
                  {/*  className="tbk-shrink-0"*/}
                  {/*  src={logo.src}*/}
                  {/*  srcSet={`${logo.src} 1x, ${logo.src2x} 2x`} //, ${logoBandier3x} 3x`}*/}
                  {/*  alt={logo.alt}*/}
                  {/*/>*/}
                  <ProgressiveImage
                    src={logo.src}
                    alt={logo.alt}
                    imgConfig={imgConfig}
                    defaultBreakpoint="xs"
                    // width="100%"
                    height="100%"
                    style={
                      {
                        // position: 'absolute',
                        // top: 0,
                        // left: 0
                      }
                    }
                    className="tbk-w-full_ tbk-h-full tbk-object-cover"
                  />
                </div>
              );
            })}
          </div>

          <div className="tbk-mb-4 tbk-items-center md:tbk-mb-0">
            <div className="tbk-flex tbk-grid-cols-2 tbk-flex-wrap tbk-gap-2 lg:tbk-grid lg:tbk-grid-cols-corporate-solutions">
              {corporateSolutions.map((solution, index) => {
                const imgConfig = [
                  {
                    breakpoint: "xs",
                    url: solution.src,
                    url2x: solution.src2x,
                  },
                  {
                    breakpoint: "md",
                    url: solution.src,
                    url2x: solution.src2x,
                  },
                  {
                    breakpoint: "lg",
                    url: solution.src,
                    url2x: solution.src2x,
                  },
                ];
                return (
                  <div
                    key={index}
                    className="tbk-relative tbk-h-45 tbk-grow tbk-basis-2/5 tbk-overflow-hidden md:tbk-h-54 lg:tbk-basis-auto"
                  >
                    <ProgressiveImage
                      src={solution.src}
                      alt={solution.alt}
                      imgConfig={imgConfig}
                      defaultBreakpoint="xs"
                      width="100%"
                      height="100%"
                      // style={{ position: "absolute", top: 0, left: 0 }}
                      className="tbk-z-1_ tbk-absolute tbk-left-0 tbk-top-0 tbk-h-full tbk-w-full tbk-overflow-hidden tbk-rounded-lg tbk-object-cover"
                      showLogo={false}
                      // logoHeight={56}
                      imgStyle={{
                        width: "100%",
                        height: "100%",
                      }}
                    />
                    <div className="tbk-text-cta md:tbk-text-title-bold tbk-absolute tbk-left-1/2 tbk-top-1/2 tbk--translate-x-1/2 tbk--translate-y-1/2 tbk-text-center tbk-uppercase tbk-text-basic-white md:tbk-normal-case">
                      {solution.multiLine ? (
                        <>
                          <span className="tbk-whitespace-nowrap">
                            Co-Working
                          </span>{" "}
                          Spaces
                        </>
                      ) : (
                        solution.text
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="tbk-flex tbk-hidden tbk-justify-center">
            <div className="tbk-w-126">
              <p className="tbk-text-main tbk-mb-0 tbk-text-center tbk-text-primary">
                TruBe specialises in delivering customised fitness and wellness
                solutions across diverse sectors. From energising events to
                promoting well-being in co-working spaces, enhancing corporate
                hospitality experiences, and providing tailored care for
                residents in care homes, TruBe offers versatile and expert
                services for a wide range of environments.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/*<GymsSlider gyms={gymsData} />*/}

      {/*<div className="tbk-flex tbk-hidden tbk-flex-col tbk-items-center tbk-justify-center tbk-overflow-hidden tbk-pb-5 tbk-pt-7 lg:tbk-pb-9 lg:tbk-pt-9">*/}
      {/*  <div className="tbk-container tbk-px-2 lg:tbk-px-8">*/}
      {/*    <div className="lg:tbk-grid lg:tbk-grid-cols-homepage-block-with-slider lg:tbk-gap-6 lg:tbk-pt-7">*/}
      {/*      <div className="">*/}
      {/*        <div className="tbk-text-h2-black lg:tbk-text-epic-medium tbk-mb-2 tbk-text-grey-main">*/}
      {/*          Download <span className="tbk-text-coral">the App</span>*/}
      {/*        </div>*/}
      {/*        <p className="tbk-text-h3-subtitle lg:tbk-text-title-medium tbk-mb-3 tbk-text-grey-main lg:tbk-mb-4">*/}
      {/*          Our seamless booking platform guarantees ease and accessibility.*/}
      {/*        </p>*/}

      {/*        <a*/}
      {/*          href="https://apps.apple.com/us/app/trube/id6449003944"*/}
      {/*          className="tbk-mb-4 tbk-inline-block lg:tbk-mb-5"*/}
      {/*          target="_blank"*/}
      {/*        >*/}
      {/*          <AppleAppStore />*/}
      {/*        </a>*/}

      {/*        <div className="tbk-mb-5 tbk-grid tbk-grid-cols-2 tbk-gap-5">*/}
      {/*          <div className="tbk-mr-3 tbk-flex">*/}
      {/*            <div className="tbk-text-small-bold tbk-mr-1 tbk-h-3 tbk-w-3 tbk-shrink-0 tbk-rounded-2xl tbk-bg-grey-main tbk-text-center !tbk-leading-loose tbk-text-basic-white">*/}
      {/*              1*/}
      {/*            </div>*/}
      {/*            <p className="tbk-text-small lg:tbk-text-secondary tbk-text-grey-main">*/}
      {/*              Review our Pillars: fitness, spirit, passion or body*/}
      {/*            </p>*/}
      {/*          </div>*/}

      {/*          <div className="tbk-mr-3 tbk-flex">*/}
      {/*            <div className="tbk-text-small-bold tbk-mr-1 tbk-h-3 tbk-w-3 tbk-shrink-0 tbk-rounded-2xl tbk-bg-grey-main tbk-text-center !tbk-leading-loose tbk-text-basic-white">*/}
      {/*              2*/}
      {/*            </div>*/}
      {/*            <p className="tbk-text-small lg:tbk-text-secondary tbk-text-grey-main">*/}
      {/*              Explore your Disciplines: boxing, weights, yoga, meditation,*/}
      {/*              and more*/}
      {/*            </p>*/}
      {/*          </div>*/}

      {/*          <div className="tbk-mr-3 tbk-flex">*/}
      {/*            <div className="tbk-text-small-bold tbk-mr-1 tbk-h-3 tbk-w-3 tbk-shrink-0 tbk-rounded-2xl tbk-bg-grey-main tbk-text-center !tbk-leading-loose tbk-text-basic-white">*/}
      {/*              3*/}
      {/*            </div>*/}
      {/*            <p className="tbk-text-small lg:tbk-text-secondary tbk-text-grey-main">*/}
      {/*              Select a Coach from profiles, reviews and ratings*/}
      {/*            </p>*/}
      {/*          </div>*/}

      {/*          <div className="tbk-flex">*/}
      {/*            <div className="tbk-text-small-bold tbk-mr-1 tbk-h-3 tbk-w-3 tbk-shrink-0 tbk-rounded-2xl tbk-bg-grey-main tbk-text-center !tbk-leading-loose tbk-text-basic-white">*/}
      {/*              4*/}
      {/*            </div>*/}
      {/*            <p className="tbk-text-small lg:tbk-text-secondary tbk-text-grey-main">*/}
      {/*              Confirm your date, time and location for an instant booking*/}
      {/*            </p>*/}
      {/*          </div>*/}
      {/*        </div>*/}
      {/*      </div>*/}

      {/*      <div>*/}
      {/*        <div className="tbk-relative tbk-mb-3 tbk-flex tbk-h-[528px]">*/}
      {/*          <Transition*/}
      {/*            in={activeSlide === 1}*/}
      {/*            timeout={500}*/}
      {/*            //addEndListener={handleTransitionEnd}*/}
      {/*          >*/}
      {/*            {(status, innerProps) => (*/}
      {/*              <div*/}
      {/*                {...innerProps}*/}
      {/*                className={`tbk-relative tbk-h-full tbk-w-66 tbk-rounded-lg tbk-transition-all tbk-duration-500 ${*/}
      {/*                  status === ENTERING*/}
      {/*                    ? slideDirection === "right"*/}
      {/*                      ? "tbk--translate-x-full"*/}
      {/*                      : ""*/}
      {/*                    : status === ENTERED*/}
      {/*                    ? "tbk-translate-x-0 tbk-opacity-100"*/}
      {/*                    : status === EXITING || status === EXITED*/}
      {/*                    ? slideDirection === "right"*/}
      {/*                      ? "tbk-opacity-5"*/}
      {/*                      : "tbk-opacity-100"*/}
      {/*                    : ""*/}
      {/*                } ${slideDirection}`}*/}
      {/*              >*/}
      {/*                <div className="tbk-absolute tbk-left-0 tbk-top-0 tbk-z-1 tbk-h-full tbk-w-full tbk-pt-[199.621212%]">*/}
      {/*                  <div*/}
      {/*                    className={`video-container tbk-absolute tbk-left-0 tbk-top-0 tbk-h-full tbk-w-full tbk-overflow-hidden tbk-rounded-[56px] tbk-px-1 tbk-pb-1 tbk-pt-1`}*/}
      {/*                  >*/}
      {/*                    <video*/}
      {/*                      id="2481656c-a859-cee8-0140-8d7702e9503e-video"*/}
      {/*                      autoPlay*/}
      {/*                      loop*/}
      {/*                      className="tbk-object-cover"*/}
      {/*                      style={{*/}
      {/*                        backgroundImage:*/}
      {/*                          "url(/homepage-slider/slider-bg-poster-00001.jpg)",*/}
      {/*                      }}*/}
      {/*                      muted*/}
      {/*                      playsInline*/}
      {/*                    >*/}
      {/*                      <source src="/homepage-slider/slider-bg-transcode.mp4" />*/}
      {/*                      <source src="/homepage-slider/slider-bg-transcode.webm" />*/}
      {/*                    </video>*/}
      {/*                  </div>*/}
      {/*                </div>*/}
      {/*              </div>*/}
      {/*            )}*/}
      {/*          </Transition>*/}
      {/*          <Transition*/}
      {/*            in={activeSlide === 2}*/}
      {/*            timeout={500}*/}
      {/*            //addEndListener={handleTransitionEnd}*/}
      {/*          >*/}
      {/*            {(status, innerProps) => (*/}
      {/*              <div*/}
      {/*                {...innerProps}*/}
      {/*                className={`tbk-absolute tbk-left-72 tbk-z-1 tbk-h-full tbk-w-66 tbk-rounded-lg tbk-transition-all tbk-duration-500 ${*/}
      {/*                  status === ENTERING*/}
      {/*                    ? "tbk--ml-3 tbk--translate-x-full"*/}
      {/*                    : status === ENTERED*/}
      {/*                    ? "tbk--ml-3 tbk--translate-x-full"*/}
      {/*                    : status === EXITING || status === EXITED*/}
      {/*                    ? slideDirection === "right"*/}
      {/*                      ? activeSlide !== 1*/}
      {/*                        ? "tbk--ml-3 tbk--translate-x-full tbk-opacity-5"*/}
      {/*                        : ""*/}
      {/*                      : activeSlide !== 1*/}
      {/*                      ? "tbk-translate-x-0"*/}
      {/*                      : ""*/}
      {/*                    : "tbk-translate-x-0"*/}
      {/*                } ${slideDirection}`}*/}
      {/*              >*/}
      {/*                <div className="tbk-absolute tbk-left-0 tbk-top-0 tbk-z-10 tbk-h-full tbk-w-full tbk-pt-[199.621212%]">*/}
      {/*                  <div className="tbk-absolute tbk-left-0 tbk-top-0 tbk-h-full tbk-w-full tbk-px-1.5 tbk-pb-1.5 tbk-pt-1.5">*/}
      {/*                    <img*/}
      {/*                      className="tbk-h-full tbk-w-full tbk-rounded-3xl tbk-border tbk-border-solid tbk-border-blue-grey-light"*/}
      {/*                      src={iphoneSliderPersonal}*/}
      {/*                      alt="iPhone"*/}
      {/*                    />*/}
      {/*                  </div>*/}
      {/*                </div>*/}
      {/*              </div>*/}
      {/*            )}*/}
      {/*          </Transition>*/}

      {/*          <Transition*/}
      {/*            in={activeSlide === 3}*/}
      {/*            timeout={500}*/}
      {/*            //addEndListener={handleTransitionEnd}*/}
      {/*          >*/}
      {/*            {(status, innerProps) => (*/}
      {/*              <div*/}
      {/*                {...innerProps}*/}
      {/*                className={`tbk-absolute tbk-left-72 tbk-z-1 tbk-h-full tbk-w-66 tbk-rounded-lg tbk-transition-all tbk-duration-500 ${*/}
      {/*                  status === ENTERING*/}
      {/*                    ? slideDirection === "right"*/}
      {/*                      ? "tbk--ml-3 tbk--translate-x-full"*/}
      {/*                      : ""*/}
      {/*                    : status === ENTERED*/}
      {/*                    ? slideDirection === "right"*/}
      {/*                      ? "tbk--ml-3 tbk--translate-x-full"*/}
      {/*                      : "tbk-translate-x-full"*/}
      {/*                    : status === EXITING || status === EXITED*/}
      {/*                    ? slideDirection === "right"*/}
      {/*                      ? activeSlide === 1*/}
      {/*                        ? "tbk-ml-3 tbk-translate-x-full"*/}
      {/*                        : ""*/}
      {/*                      : activeSlide === 1*/}
      {/*                      ? "tbk-ml-3 tbk-translate-x-full"*/}
      {/*                      : ""*/}
      {/*                    : "tbk-translate-x-0"*/}
      {/*                } ${slideDirection}`}*/}
      {/*              >*/}
      {/*                <div className="tbk-absolute tbk-left-0 tbk-top-0 tbk-z-20 tbk-h-full tbk-w-full tbk-pt-[199.621212%]">*/}
      {/*                  <div className="tbk-absolute tbk-left-0 tbk-top-0 tbk-h-full tbk-w-full tbk-px-1.5 tbk-pb-1.5 tbk-pt-1.5">*/}
      {/*                    <img*/}
      {/*                      className="tbk-h-full tbk-w-full tbk-rounded-3xl tbk-border tbk-border-solid tbk-border-blue-grey-light"*/}
      {/*                      src={iphoneSliderLp}*/}
      {/*                      alt="iPhone"*/}
      {/*                    />*/}
      {/*                  </div>*/}
      {/*                </div>*/}
      {/*              </div>*/}
      {/*            )}*/}
      {/*          </Transition>*/}
      {/*          <div className="tbk-absolute tbk-left-0 tbk-top-0 tbk-z-1 tbk-h-full tbk-w-full tbk-bg-iphone-frame tbk-bg-contain tbk-bg-left-top tbk-bg-no-repeat" />*/}
      {/*        </div>*/}
      {/*        <button*/}
      {/*          className={`tbk-ml-7 tbk-mr-5 tbk-rounded-full tbk-p-1 tbk-text-grey-main ${*/}
      {/*            activeSlide === 1 ? "tbk-opacity-50" : "tbk-shadow-trube "*/}
      {/*          }`}*/}
      {/*          disabled={activeSlide === 1}*/}
      {/*          onClick={handleMoveSliderLeft}*/}
      {/*        >*/}
      {/*          <ArrowLeftIcon size={32} />*/}
      {/*        </button>*/}
      {/*        <button*/}
      {/*          className={`tbk-rounded-full tbk-p-1 tbk-text-grey-main ${*/}
      {/*            activeSlide === 3 ? "tbk-opacity-50" : "tbk-shadow-trube "*/}
      {/*          }`}*/}
      {/*          disabled={activeSlide === 3}*/}
      {/*          onClick={handleMoveSliderRight}*/}
      {/*        >*/}
      {/*          <ArrowRightIcon size={32} />*/}
      {/*        </button>*/}
      {/*      </div>*/}
      {/*    </div>*/}
      {/*  </div>*/}
      {/*</div>*/}

      {false && (
        <div className="tbk-flex tbk-hidden tbk-flex-col tbk-items-center tbk-justify-center tbk-pb-7 tbk-pt-7 lg:tbk-pb-9 lg:tbk-pt-9">
          <div className="tbk-container tbk-px-3 lg:tbk-px-8">
            <HeadlineWithSeparator
              component="h3"
              titleClass="tbk-text-title-bold tbk-bg-basic-white tbk-text-blue-grey"
              className="tbk-mb-6 tbk-mt-5"
              separatorClass={"tbk-mb-0 tbk--mt-2"}
            >
              How it works
            </HeadlineWithSeparator>

            <div className="tbk-mb-7 md:tbk-grid md:tbk-grid-cols-2 md:tbk-gap-2 lg:tbk-grid-cols-4">
              <div className="tbk-mb-5 lg:tbk-mb-0">
                <div className="tbk-relative tbk-overflow-hidden tbk-rounded-2xl">
                  <img
                    className="tbk-w-full"
                    src={accessImg}
                    srcSet={`${access2xImg} 2x, ${access3xImg} 3x`}
                    alt="Access"
                  />
                  <HowItWorks1 className="tbk-absolute tbk-left-1/2 tbk-top-1/2 tbk--translate-x-1/2 tbk--translate-y-1/2" />
                  <div className="tbk-text-title-bold tbk-absolute tbk-left-1/2 tbk-top-1/2 tbk--translate-x-1/2 tbk--translate-y-1/2 tbk-text-center tbk-text-basic-white">
                    Access
                  </div>
                </div>
                <div className="tbk-text-h3-subtitle tbk-px-3 tbk-pb-1 tbk-pt-2 tbk-text-center tbk-text-primary">
                  Access and book the best coaches near you
                </div>
              </div>
              <div className="tbk-mb-5 lg:tbk-mb-0">
                <div className="tbk-relative tbk-overflow-hidden tbk-rounded-2xl">
                  <img
                    className="tbk-w-full"
                    src={connectImg}
                    srcSet={`${connect2xImg} 2x, ${connect3xImg} 3x`}
                    alt="Connect"
                  />
                  <HowItWorks2 className="tbk-absolute tbk-left-1/2 tbk-top-1/2 tbk--translate-x-1/2 tbk--translate-y-1/2" />
                  <div className="tbk-text-title-bold tbk-absolute tbk-left-1/2 tbk-top-1/2 tbk--translate-x-1/2 tbk--translate-y-1/2 tbk-text-center tbk-text-basic-white">
                    Connect
                  </div>
                </div>
                <div className="tbk-text-h3-subtitle tbk-px-3 tbk-pb-1 tbk-pt-2 tbk-text-center tbk-text-primary">
                  Connect via text and develop the best session for you
                </div>
              </div>

              <div className="tbk-mb-5 lg:tbk-mb-0">
                <div className="tbk-relative tbk-overflow-hidden tbk-rounded-2xl">
                  <img
                    className="tbk-w-full"
                    src={trainImg}
                    srcSet={`${train2xImg} 2x, ${train3xImg} 3x`}
                    alt="Train"
                  />
                  <HowItWorks3 className="tbk-absolute tbk-left-1/2 tbk-top-1/2 tbk--translate-x-1/2 tbk--translate-y-1/2" />
                  <div className="tbk-text-title-bold tbk-absolute tbk-left-1/2 tbk-top-1/2 tbk--translate-x-1/2 tbk--translate-y-1/2 tbk-text-center tbk-text-basic-white">
                    Train
                  </div>
                </div>
                <div className="tbk-text-h3-subtitle tbk-px-3 tbk-pb-1 tbk-pt-2 tbk-text-center tbk-text-primary">
                  Train 1 on 1 with continuous analysis & feedback
                </div>
              </div>

              <div className="">
                <div className="tbk-relative tbk-overflow-hidden tbk-rounded-2xl">
                  <img
                    className="tbk-w-full"
                    src={progressImg}
                    srcSet={`${progress2xImg} 2x, ${progress3xImg} 3x`}
                    alt="Progress"
                  />
                  <HowItWorks4 className="tbk-absolute tbk-left-1/2 tbk-top-1/2 tbk--translate-x-1/2 tbk--translate-y-1/2" />
                  <div className="tbk-text-title-bold tbk-absolute tbk-left-1/2 tbk-top-1/2 tbk--translate-x-1/2 tbk--translate-y-1/2 tbk-text-center tbk-text-basic-white">
                    Progress
                  </div>
                </div>
                <div className="tbk-text-h3-subtitle tbk-px-3 tbk-pt-2 tbk-text-center tbk-text-primary">
                  Progress with the same coach and achieve your goals together
                </div>
              </div>
            </div>
            <Link
              className="tbk-block tbk-text-center"
              to={isUserLoggedIn || true ? "/partners" : "/become-a-member"}
            >
              <Button
                variant="secondary"
                className="tbk-inline-flex tbk-w-66 tbk-pl-5 tbk-pr-5"
                endIcon={<ArrowRightIcon className="tbk-ml-0.5" />}
              >
                Start now
              </Button>
            </Link>
          </div>
        </div>
      )}

      <section className="tbk-relative tbk-hidden tbk-overflow-hidden tbk-bg-blue-grey">
        <div className="tbk-flex tbk-flex-col tbk-justify-evenly md:tbk-grid md:tbk-grid-cols-2">
          <div className="tbk-justify-self-end tbk-bg-blue-grey tbk-px-2 tbk-pb-7 tbk-pt-7 lg:tbk-w-160 lg:tbk-p-8 lg:tbk-pb-8 lg:tbk-pt-8">
            <div className="tbk-text-h2-black lg:tbk-text-epic-medium tbk-mb-3 tbk-text-basic-white lg:tbk-ml-9 lg:tbk-mt-0">
              TruBe is <span className="tbk-text-coral-light">Lifestyle</span>
            </div>
            <p className="tbk-text-main tbk-mb-4 tbk-text-basic-white lg:tbk-ml-9">
              Born in London with growing success, we have expanded into North
              America, launching in Los Angeles with more cities to follow.
              <br />
              <br />
              Our members are built on the pillars of community, integrity, and
              togetherness, where we stand united in our pursuit. We are not
              just an organization; we are your support system, here to guide
              you on your journey. We are committed to helping you break free
              from self-imposed limits, fostering personal growth, and enhancing
              your overall well-being. Together, we'll push the boundaries of
              what's possible.
            </p>

            <Button
              variant="secondary"
              className="tbk-inline-flex tbk-w-full tbk-whitespace-nowrap tbk-pl-1 tbk-pr-4 lg:tbk-ml-9 lg:tbk-w-auto lg:tbk-pl-5 lg:tbk-pr-5"
              endIcon={<ArrowRightIcon className="tbk-ml-0.5" />}
              onClick={() => setIsAskConciergeModalOpen(true)}
            >
              Speak with your TruBe Concierge
            </Button>
          </div>

          <div className="tbk-relative">
            <picture>
              <source
                media="(min-width: 960px)"
                srcSet={`${trubeIsLifestyle} 1x, ${trubeIsLifestyle2x} 2x, ${trubeIsLifestyle3x} 3x`}
              />
              <source
                srcSet={`${trubeIsLifestyleMobile} 1x, ${trubeIsLifestyleMobile2x} 2x, ${trubeIsLifestyleMobile3x} 3x`}
              />
              <img
                className="tbk-h-full tbk-w-full tbk-object-cover tbk-object-left-top lg:tbk-absolute"
                role="presentation"
                src={trubeIsLifestyle}
                alt="TruBe is Lifestyle"
              />
            </picture>
          </div>
        </div>
      </section>

      <AppBanner />

      <AskConciergeModal
        isOpen={isAskConciergeModalOpen}
        onClose={() => setIsAskConciergeModalOpen(false)}
      />

      <Footer region={region} />

      <Modal
        isOpen={isGalleryCarouselModalOpen}
        // onAfterOpen={this.afterOpenModal}
        onRequestClose={closeGalleryCarouselModal.bind(null)}
        style={modalStyles.galleryCarouselDefault}
        contentLabel="Gallery Carousel"
      >
        {/*<Link role='button' onClick={this.closeDateTimeSelectorModal}><Icon name='close' className='tb-pd-0' width='32px' height='32px' /></Link>*/}
        <div
          role="button"
          className="tbk-absolute tbk-right-2 tbk-top-2 tbk-z-10 tbk-p-1"
          onClick={closeGalleryCarouselModal}
        >
          <CloseIcon className="tbk-p-0 tbk-text-basic-white" />
        </div>
        <div className="modal-gallery-carousel u-d-flex_ u-flex-column_ tbk-h-full">
          <Carousel
            // images={images}
            hasIndexBoard={false}
            hasMediaButton={false}
            hasSizeButton={false}
            hasThumbnails={false}
            index={galleryInitialSlide}
            leftIcon={
              <svg
                width="8"
                height="13"
                viewBox="0 0 8 13"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M8 1.5L6.5 0L0 6.5L6.5 13L8 11.5L3 6.5L8 1.5Z"
                  fill="#CBCFD5"
                />
              </svg>
            }
            rightIcon={
              <svg
                width="8"
                height="13"
                viewBox="0 0 8 13"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M0 1.5L1.5 0L8 6.5L1.5 13L0 11.5L5 6.5L0 1.5Z"
                  fill="#CBCFD5"
                />
              </svg>
            }
            style={{ height: "100%", width: "100%" }}
            // onIndexChange={({ curIndex, curIndexForDisplay }) => {
            //   const ytPlayerIframes = document.querySelectorAll(".yt_player_iframe");
            // }}
          >
            {galleryImages &&
              galleryImages.map((url) => (
                <figure className="_3iVQ0">
                  <img
                    className="_t1897 tbk-h-auto_ tbk-top-1/2 tbk-mx-auto tbk-w-auto tbk--translate-y-1/2 tbk-object-contain"
                    src={url}
                  />
                </figure>
              ))}
          </Carousel>
        </div>
      </Modal>

      <Modal
        appElement={/*__CLIENT__ && */ document.getElementById("#app")}
        isOpen={isInfoModalOpen}
        // onAfterOpen={afterOpenInfoModal}
        onRequestClose={closeInfoModal}
        style={
          clientWidth() < theme.responsive.breakpoints.sm
            ? modalStyles.infoMobile
            : modalStyles.info
        }
        contentLabel="Info Modal"
      >
        <div className="modal-info tbk-flex tbk-flex-col tbk-items-center">
          {/*<button*/}
          {/*  onClick={closeInfoModal}*/}
          {/*  className="tbk-absolute tbk-top-2 tbk-right-2 tbk-z-10 tbk-hidden tbk-text-coral"*/}
          {/*>*/}
          {/*  <CloseIcon />*/}
          {/*</button>*/}
          <h3 className="tbk-text-title-bold tbk-mb-1 tbk-mt-0 tbk-text-center tbk-text-grey-main">
            {infoModalTitle}
          </h3>
          <p className="tbk-text-main tbk-mb-4 tbk-max-w-full tbk-text-center tbk-text-grey-main">
            {infoModalMsg}
          </p>
          {infoModalTitle === "Referral Bonus Applied" ? (
            <CheckNiceIcon size={40} className="tbk-mb-4 tbk-text-green" />
          ) : (
            <BlockerIcon size={40} className="tbk-mb-4 tbk-text-green" />
          )}
          <Button className="tbk-w-full" onClick={closeInfoModal}>
            Ok
          </Button>
        </div>
      </Modal>
    </>
  );
}
